import * as React from 'react';
import { type ReactNode, createContext, useCallback, useEffect, useState } from 'react';

import type { WeeklyStreaks_StreakFragment as WeeklyStreakFragment } from 'bundles/daily-goals/__queries__/__generated__/queryWeeklyStreaks';
import { useAutoMarkSetUpLearningPlanGoalAsCompleted } from 'bundles/daily-goals/hooks/useAutoMarkSetUpLearningPlanGoalAsCompleted';
import useQueryDailyGoals from 'bundles/daily-goals/hooks/useQueryDailyGoals';
import useQueryWeeklyStreaks from 'bundles/daily-goals/hooks/useQueryWeeklyStreaks';
import type {
  DailyGoal,
  DailyGoalTypeNameString,
  LearningAssistantMessage,
  WeeklyStreakCelebrationData,
  WeeklyStreakProgressToastData,
} from 'bundles/daily-goals/types';
import { processLearningAssistantMessages } from 'bundles/daily-goals/utils/dailyGoalLearningAssistantUtils';
import { isAllDailyGoalsCompleted } from 'bundles/daily-goals/utils/dailyGoalsUtils';
import { useLearnerGoals } from 'bundles/goal-setting/components/unified-goals/UnifiedGoalsDataProvider';
import type { LearningAssistanceMessage } from 'bundles/learning-assistant/types/RealtimeMessages';

export type DailyGoalsDataProviderProps = {
  activeCourseId: string | undefined;
  moduleId: string | undefined;
  nextModuleResourcePath: string | undefined;
  messages: LearningAssistanceMessage[];
  ssr?: boolean;
  children: ReactNode;
};

export type DailyGoalsDataContextProps = {
  activeCourseId: string | undefined;
  dailyGoals?: Array<DailyGoal>;
  dayOfLearning?: number;
  dailyGoalsToastIds?: DailyGoalTypeNameString[];
  setDailyGoalsToastIds: React.Dispatch<React.SetStateAction<DailyGoalTypeNameString[] | undefined>>;
  pushToastId: (newToastId: DailyGoalTypeNameString) => void;
  removeFirstToastId: () => void;
  allDailyGoalsCompleted: boolean;
  loading: boolean;
  moduleCompletionLearningAssistantMessage: LearningAssistantMessage | undefined;
  setModuleCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistantMessage | undefined>
  >;
  courseCompletionLearningAssistantMessage: LearningAssistantMessage | undefined;
  setCourseCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistantMessage | undefined>
  >;
  nextModuleResourcePath: string | undefined;
  showAllDailyGoalsCompletedModal: boolean;
  setShowAllDailyGoalsCompletedModal: React.Dispatch<React.SetStateAction<boolean>>;
  weeklyStreak: WeeklyStreakFragment | undefined;
  weeklyStreakCelebrationData: WeeklyStreakCelebrationData | undefined;
  clearWeeklyStreakCelebrationData: () => void;
  weeklyStreakProgressToastData: WeeklyStreakProgressToastData | undefined;
  clearWeeklyStreakProgressToastData: () => void;
  weeklyStreaksLoading: boolean;
};

const DailyGoalsDataContext = createContext<DailyGoalsDataContextProps>({
  activeCourseId: undefined,
  dailyGoals: undefined,
  dayOfLearning: undefined,
  dailyGoalsToastIds: undefined,
  setDailyGoalsToastIds: () => {},
  pushToastId: () => {},
  removeFirstToastId: () => {},
  allDailyGoalsCompleted: false,
  loading: false,
  moduleCompletionLearningAssistantMessage: undefined,
  setModuleCompletionLearningAssistantMessage: () => {},
  courseCompletionLearningAssistantMessage: undefined,
  setCourseCompletionLearningAssistantMessage: () => {},
  nextModuleResourcePath: undefined,
  showAllDailyGoalsCompletedModal: false,
  setShowAllDailyGoalsCompletedModal: () => {},
  weeklyStreak: undefined,
  weeklyStreakCelebrationData: undefined,
  clearWeeklyStreakCelebrationData: () => {},
  weeklyStreakProgressToastData: undefined,
  clearWeeklyStreakProgressToastData: () => {},
  weeklyStreaksLoading: false,
});

// Export the context for testing
export { DailyGoalsDataContext };

export const DailyGoalsDataProvider = ({
  activeCourseId,
  moduleId,
  children,
  messages,
  nextModuleResourcePath,
  ssr,
}: DailyGoalsDataProviderProps) => {
  const { dailyGoals: queryDailyGoals, dayOfLearning, loading } = useQueryDailyGoals(activeCourseId, { ssr });
  const { weeklyStreak, loading: weeklyStreaksLoading } = useQueryWeeklyStreaks({ ssr });
  const { learnerGoals } = useLearnerGoals();
  const [dailyGoals, setDailyGoals] = useState<Array<DailyGoal> | undefined>(queryDailyGoals);
  const [dayOfLearningState, setDayOfLearningState] = useState<number | undefined>(dayOfLearning);
  const [allDailyGoalsCompleted, setAllDailyGoalsCompleted] = useState(isAllDailyGoalsCompleted(queryDailyGoals));
  const [dailyGoalsToastIds, setDailyGoalsToastIds] = useState<DailyGoalTypeNameString[] | undefined>(undefined);
  const [moduleCompletionLearningAssistantMessage, setModuleCompletionLearningAssistantMessage] = useState<
    LearningAssistantMessage | undefined
  >(undefined);
  const [courseCompletionLearningAssistantMessage, setCourseCompletionLearningAssistantMessage] = useState<
    LearningAssistantMessage | undefined
  >(undefined);
  const [showAllDailyGoalsCompletedModal, setShowAllDailyGoalsCompletedModal] = useState(false);
  const [weeklyStreakCelebrationData, setWeeklyStreakCelebrationData] = useState<
    WeeklyStreakCelebrationData | undefined
  >(undefined);
  const [weeklyStreakProgressToastData, setWeeklyStreakProgressToastData] = useState<
    WeeklyStreakProgressToastData | undefined
  >(undefined);

  const pushToastId = useCallback<(newToastId: DailyGoalTypeNameString) => void>(
    (newToastId) => {
      setDailyGoalsToastIds((prevToastIds) => {
        if (!prevToastIds) return [newToastId];
        return [...prevToastIds, newToastId];
      });
    },
    [setDailyGoalsToastIds]
  );

  const removeFirstToastId = useCallback<() => void>(() => {
    setDailyGoalsToastIds((prevToastIds) => {
      if (!prevToastIds) return prevToastIds;
      return prevToastIds.slice(1);
    });
  }, [setDailyGoalsToastIds]);

  const clearWeeklyStreakCelebrationData = useCallback<() => void>(() => {
    setWeeklyStreakCelebrationData(undefined);
  }, [setWeeklyStreakCelebrationData]);

  const clearWeeklyStreakProgressToastData = useCallback<() => void>(() => {
    setWeeklyStreakProgressToastData(undefined);
  }, [setWeeklyStreakProgressToastData]);

  // Update local state when query data changes
  // NOTE This is an anitpattern - storing the return value from the query in local state
  // This creates multiple sources of truth, which has led to bugs already.
  // Ticket to address this issue: https://coursera.atlassian.net/browse/LRNRSCCSS-4977
  useEffect(() => {
    if (queryDailyGoals) {
      setDailyGoals([...queryDailyGoals]);
    }
    if (dayOfLearning) {
      setDayOfLearningState(dayOfLearning);
    }
  }, [queryDailyGoals, dayOfLearning]);

  // Process learning assistant messages and update daily goals
  useEffect(() => {
    if (messages?.length > 0) {
      processLearningAssistantMessages({
        messages,
        currentGoals: dailyGoals ?? [],
        moduleId,
        learnerGoals,
        setDailyGoals,
        setModuleCompletionLearningAssistantMessage,
        setCourseCompletionLearningAssistantMessage,
        pushToastId,
        setWeeklyStreakCelebrationData,
        setWeeklyStreakProgressToastData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when messages change
  }, [messages]);

  // Check if all daily goals are completed whenever dailyGoals changes
  useEffect(() => {
    if (dailyGoals && isAllDailyGoalsCompleted(dailyGoals)) {
      const areDailyGoalsCompleted = isAllDailyGoalsCompleted(dailyGoals);
      setAllDailyGoalsCompleted(areDailyGoalsCompleted);
      setShowAllDailyGoalsCompletedModal(areDailyGoalsCompleted);
    }
  }, [dailyGoals]);

  useAutoMarkSetUpLearningPlanGoalAsCompleted({
    activeCourseId,
    dailyGoals,
    pushToastId,
  });

  return (
    <DailyGoalsDataContext.Provider
      value={{
        dailyGoals,
        dayOfLearning: dayOfLearningState,
        dailyGoalsToastIds,
        setDailyGoalsToastIds,
        pushToastId,
        removeFirstToastId,
        allDailyGoalsCompleted,
        loading,
        moduleCompletionLearningAssistantMessage,
        setModuleCompletionLearningAssistantMessage,
        courseCompletionLearningAssistantMessage,
        setCourseCompletionLearningAssistantMessage,
        nextModuleResourcePath,
        showAllDailyGoalsCompletedModal,
        setShowAllDailyGoalsCompletedModal,
        weeklyStreak,
        weeklyStreakCelebrationData,
        clearWeeklyStreakCelebrationData,
        weeklyStreakProgressToastData,
        clearWeeklyStreakProgressToastData,
        weeklyStreaksLoading,
        activeCourseId,
      }}
    >
      {children}
    </DailyGoalsDataContext.Provider>
  );
};

export const useDailyGoalsData = () => {
  const context = React.useContext(DailyGoalsDataContext);

  if (context === undefined) {
    throw new Error('useDailyGoalsData must be used within a DailyGoalsDataProvider');
  }

  return context;
};
